import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Trash2, Send, ArrowRight, Package, ChevronDown, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `/api`;

const InquiryPage = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]); // {variant_id, group_name, color_name, lot_no, quantity, max_rolls}
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", message: ""
  });

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API}/groups`);
        setGroups(res.data);
      } catch {
        // Error fetching groups
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, []);

  // Fetch variants when group changes
  useEffect(() => {
    if (!selectedGroupId) { setVariants([]); return; }
    const fetchVariants = async () => {
      setVariantsLoading(true);
      try {
        const res = await axios.get(`${API}/variants?group_id=${selectedGroupId}`);
        // Only show variants that have stock
        setVariants(res.data.filter(v => v.stock_rolls > 0));
      } catch { setVariants([]); }
      finally { setVariantsLoading(false); }
    };
    fetchVariants();
  }, [selectedGroupId]);

  const handleAddVariant = (variant) => {
    if (selectedItems.some(i => i.variant_id === variant.id)) {
      toast.info("This colour is already in your inquiry");
      return;
    }
    const group = groups.find(g => g.id === variant.group_id);
    setSelectedItems(prev => [...prev, {
      variant_id: variant.id,
      group_id: variant.group_id,
      group_name: group?.name || "",
      color_name: variant.color_name,
      hex_code: variant.hex_code,
      lot_no: variant.lot_no || "",
      quantity: 1,
      max_rolls: variant.stock_rolls
    }]);
    toast.success(`${variant.color_name} added`);
  };

  const handleRemoveItem = (variantId) => {
    setSelectedItems(prev => prev.filter(i => i.variant_id !== variantId));
  };

  const handleQuantityChange = (variantId, qty) => {
    setSelectedItems(prev => prev.map(item => {
      if (item.variant_id !== variantId) return item;
      const clamped = Math.max(1, Math.min(qty, item.max_rolls));
      return { ...item, quantity: clamped };
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (selectedItems.length === 0) {
      toast.error("Please select at least one colour");
      return;
    }
    // Validate quantities
    for (const item of selectedItems) {
      if (item.quantity > item.max_rolls) {
        toast.error(`${item.color_name}: quantity (${item.quantity}) exceeds available stock (${item.max_rolls} rolls)`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const selected_products = selectedItems.map(i => ({
        product_id: i.variant_id,
        product_name: `${i.group_name} - ${i.color_name}${i.lot_no ? ` (${i.lot_no})` : ''}`,
        quantity: i.quantity,
        unit: "rolls"
      }));
      const inquiryData = {
        ...formData,
        selected_products,
        product_interest: selected_products.map(p => p.product_name).join(", "),
        message: formData.message || `Inquiry for: ${selected_products.map(p => `${p.product_name} x${p.quantity} rolls`).join(", ")}`
      };
      await axios.post(`${API}/inquiries`, inquiryData);
      setSubmitted(true);
      toast.success("Your inquiry has been submitted!");
    } catch {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="inquiry-success">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-[#4A5D4E] rounded-full">
            <Send size={32} className="text-white" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-normal text-[#1C1C1A] mb-4">Inquiry Submitted!</h1>
          <p className="text-[#6B6A65] font-light mb-8">
            Thank you for your inquiry. Our team will review your request and get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="btn-primary">Continue Shopping</Link>
            <Link to="/" className="btn-secondary">Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  const selectedGroup = groups.find(g => g.id === selectedGroupId);

  return (
    <div className="min-h-screen" data-testid="inquiry-page">
      <Helmet>
        <title>Send a Product Inquiry | DUGARS</title>
        <meta name="description" content="Request a quote for DUGARS fabrics. Select from our collections, specify quantities, and we'll get back to you promptly." />
        <link rel="canonical" href="https://thedugars.in/inquiry" />
        <meta property="og:title" content="Send a Product Inquiry | DUGARS" />
        <meta property="og:description" content="Request a quote for DUGARS fabrics." />
        <meta property="og:url" content="https://thedugars.in/inquiry" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">Product Inquiry</span>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#1C1C1A]">Send Your Inquiry</h1>
            <p className="text-[#6B6A65] font-light mt-6">
              Select a product group, choose colours, and specify the quantity you need (limited to available stock).
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

            {/* Left — Group + Colour Selection */}
            <div className="lg:col-span-7 space-y-8">
              {/* Step 1: Select Group */}
              <div>
                <h2 className="font-['Cormorant_Garamond'] text-2xl font-normal text-[#1C1C1A] mb-4">
                  1. Select Product Group
                </h2>
                {loading ? (
                  <div className="animate-pulse h-12 bg-[#E6E4DD] w-full" />
                ) : (
                  <div className="relative">
                    <select
                      value={selectedGroupId}
                      onChange={e => setSelectedGroupId(e.target.value)}
                      className="custom-input w-full appearance-none pr-10 cursor-pointer"
                      data-testid="group-select"
                    >
                      <option value="">-- Choose a product group --</option>
                      {groups.map(g => (
                        <option key={g.id} value={g.id}>{g.name} ({g.variant_count || 0} colours)</option>
                      ))}
                    </select>
                    <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6A65] pointer-events-none" />
                  </div>
                )}
              </div>

              {/* Step 2: Select Colours */}
              {selectedGroupId && (
                <div>
                  <h2 className="font-['Cormorant_Garamond'] text-2xl font-normal text-[#1C1C1A] mb-4">
                    2. Select Colours
                    {selectedGroup && <span className="text-lg text-[#6B6A65] ml-2">— {selectedGroup.name}</span>}
                  </h2>

                  {variantsLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="animate-pulse p-4 border border-[#E6E4DD]">
                          <div className="flex gap-3"><div className="w-10 h-10 bg-[#E6E4DD]" /><div className="flex-1"><div className="h-4 bg-[#E6E4DD] w-2/3 mb-2" /><div className="h-3 bg-[#E6E4DD] w-1/3" /></div></div>
                        </div>
                      ))}
                    </div>
                  ) : variants.length === 0 ? (
                    <div className="text-center py-12 border border-dashed border-[#E6E4DD]">
                      <Package size={36} className="mx-auto mb-3 text-[#6B6A65]/30" />
                      <p className="text-[#6B6A65]">No colours with available stock in this group.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="variant-selection-grid">
                      {variants.map(v => {
                        const isAdded = selectedItems.some(i => i.variant_id === v.id);
                        return (
                          <button
                            key={v.id}
                            onClick={() => !isAdded && handleAddVariant(v)}
                            disabled={isAdded}
                            className={`flex items-center gap-3 p-4 border text-left transition-all ${
                              isAdded ? 'border-[#4A5D4E] bg-[#4A5D4E]/5' : 'border-[#E6E4DD] hover:border-[#4A5D4E]/50 hover:bg-[#F7F5F0]'
                            }`}
                            data-testid={`select-variant-${v.id}`}
                          >
                            {/* Colour swatch */}
                            {v.image_url ? (
                              <img src={v.image_url} alt="" className="w-10 h-10 object-cover border border-[#E6E4DD] flex-shrink-0" />
                            ) : (
                              <div className="w-10 h-10 border border-[#E6E4DD] flex-shrink-0" style={{ backgroundColor: v.hex_code || '#ccc' }} />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1C1C1A] truncate">{v.color_name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {v.lot_no && <span className="text-[10px] text-[#6B6A65]">Lot: {v.lot_no}</span>}
                                <span className="text-[10px] text-green-700 font-medium">{v.stock_rolls} roll{v.stock_rolls !== 1 ? 's' : ''} avail.</span>
                                {v.discount_percent && <span className="text-[10px] font-bold text-red-600">{v.discount_percent}% OFF</span>}
                                {!v.discount_percent && v.discount_label && <span className="text-[10px] font-bold text-red-600">{v.discount_label}</span>}
                              </div>
                            </div>
                            {isAdded && (
                              <div className="w-6 h-6 bg-[#4A5D4E] flex items-center justify-center flex-shrink-0">
                                <Check size={14} className="text-white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right — Inquiry Form */}
            <div className="lg:col-span-5">
              <div className="bg-white p-6 md:p-8 border border-[#E6E4DD] sticky top-24">
                <h2 className="font-['Cormorant_Garamond'] text-2xl font-normal text-[#1C1C1A] mb-6">Your Inquiry</h2>

                {/* Selected Items */}
                {selectedItems.length > 0 && (
                  <div className="mb-6 pb-6 border-b border-[#E6E4DD]">
                    <h3 className="text-sm font-medium text-[#1C1C1A] mb-3">
                      Selected Colours ({selectedItems.length})
                    </h3>
                    <div className="space-y-3" data-testid="selected-items-list">
                      {selectedItems.map(item => (
                        <div key={item.variant_id} className="p-3 bg-[#F7F5F0]">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-5 h-5 border border-[#E6E4DD] flex-shrink-0" style={{ backgroundColor: item.hex_code || '#ccc' }} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1C1C1A] truncate">{item.group_name} — {item.color_name}</p>
                              {item.lot_no && <p className="text-[10px] text-[#6B6A65]">Lot: {item.lot_no}</p>}
                            </div>
                            <button
                              onClick={() => handleRemoveItem(item.variant_id)}
                              className="p-1 text-red-500 hover:bg-red-50 flex-shrink-0"
                              data-testid={`remove-item-${item.variant_id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-[#6B6A65] whitespace-nowrap">Rolls:</label>
                            <input
                              type="number"
                              min={1}
                              max={item.max_rolls}
                              value={item.quantity}
                              onChange={e => handleQuantityChange(item.variant_id, parseInt(e.target.value) || 1)}
                              className="w-20 text-center text-sm border border-[#E6E4DD] py-1"
                              data-testid={`qty-input-${item.variant_id}`}
                            />
                            <span className="text-xs text-[#6B6A65]">/ {item.max_rolls} avail.</span>
                            {item.quantity > item.max_rolls && (
                              <span className="text-xs text-red-600 font-medium">Exceeds stock!</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Form */}
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="inquiry-form">
                  <div>
                    <label className="block text-sm font-medium text-[#1C1C1A] mb-2">Full Name <span className="text-red-500">*</span></label>
                    <input type="text" name="name" value={formData.name} onChange={handleFormChange} className="custom-input w-full" placeholder="Your name" required data-testid="inquiry-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1C1A] mb-2">Email <span className="text-red-500">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleFormChange} className="custom-input w-full" placeholder="your@email.com" required data-testid="inquiry-email-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1C1A] mb-2">Phone <span className="text-red-500">*</span></label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} className="custom-input w-full" placeholder="+91 XXXXXXXXXX" required data-testid="inquiry-phone-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1C1A] mb-2">Company</label>
                    <input type="text" name="company" value={formData.company} onChange={handleFormChange} className="custom-input w-full" placeholder="Your company name" data-testid="inquiry-company-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1C1C1A] mb-2">Additional Notes</label>
                    <textarea name="message" value={formData.message} onChange={handleFormChange} rows={3} className="custom-input w-full resize-none" placeholder="Any specific requirements..." data-testid="inquiry-message-input" />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || selectedItems.length === 0}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="inquiry-submit-btn"
                  >
                    {submitting ? (
                      <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</>
                    ) : (
                      <>Send Inquiry <ArrowRight size={18} /></>
                    )}
                  </button>

                  {selectedItems.length === 0 && (
                    <p className="text-xs text-[#6B6A65] text-center">Select a product group and choose colours to send an inquiry</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InquiryPage;
